import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type NotificationTone = "success" | "error" | "warning" | "info";

interface NotificationItem {
  id: number;
  tone: NotificationTone;
  title: string;
  message?: string;
}

interface NotificationContextValue {
  notify: (notification: Omit<NotificationItem, "id">) => void;
}

const NotificationContext = createContext<NotificationContextValue>({ notify: () => undefined });

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const sequence = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback(
    (notification: Omit<NotificationItem, "id">) => {
      const id = ++sequence.current;
      setItems((current) => {
        const duplicate = current.some(
          (item) =>
            item.tone === notification.tone &&
            item.title === notification.title &&
            item.message === notification.message,
        );
        return duplicate ? current : [...current.slice(-2), { ...notification, id }];
      });
      if (notification.tone === "success" || notification.tone === "info") {
        window.setTimeout(() => dismiss(id), 4000);
      }
    },
    [dismiss],
  );

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="notification-viewport" aria-live="polite" aria-relevant="additions">
        {items.map((item) => {
          const Icon =
            item.tone === "success"
              ? CheckCircle2
              : item.tone === "error"
                ? AlertCircle
                : item.tone === "warning"
                  ? AlertTriangle
                  : Info;
          return (
            <article
              className={`app-notification notification-${item.tone}`}
              key={item.id}
              role={item.tone === "error" ? "alert" : "status"}
            >
              <Icon aria-hidden="true" />
              <div>
                <strong>{item.title}</strong>
                {item.message ? <p>{item.message}</p> : null}
              </div>
              <button type="button" onClick={() => dismiss(item.id)} aria-label="Cerrar aviso">
                <X aria-hidden="true" />
              </button>
            </article>
          );
        })}
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
